import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Breadcrumbs, Button, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Sidebar from "../../components/Sidebar";
import { useProjectContext } from "../../context/ProjectContext";
import { useUserContext } from "../../context/UserContext"; // Import the UserContext
import { getAllProjects, getUserById } from "../../client/api"; // Import the API methods
import { THEME_COLORS } from "../../context/constants";
import Cookies from "js-cookie";
import { isFreeUser } from "../../utils/utils";

const MainLayout = () => {
  const location = useLocation();
  const [pathnames, setPathNames] = useState<String[]>(
    location.pathname.split("/").filter((x) => x)
  );
  const { currentProject, setProjects } = useProjectContext(); // Get the setProjects function from context
  const { user, setUser, currentWorkspace, setCurrentWorkspace } =
    useUserContext(); // Get the setUser function from UserContext
  const { projectId, videoPageId } = useParams();
  const navigate = useNavigate();
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  // Check if user should see the upgrade banner
  useEffect(() => {
    if (currentWorkspace) {
      const shouldShow =
        isFreeUser(currentWorkspace) &&
        !location.pathname.includes("/plan-selection");
      setShowUpgradeBanner(shouldShow);
    }
  }, [currentWorkspace, location.pathname]);

  const fetchProjects = async (workspaceId: string) => {
    if (!workspaceId) return;
    try {
      const projectsData = await getAllProjects(1, 10, "", workspaceId); // Fetch projects from API
      setProjects(projectsData.projects); // Assuming the API returns projects in a 'projects' key
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const authInfo = Cookies.get("authInfo");

      // Parse the cookie data to extract the userId
      const userId = authInfo ? JSON.parse(authInfo).userId : null;
      if (userId) {
        try {
          const userData = await getUserById(userId); // Fetch user from API
          setUser(userData); // Set the user in the context

          if (!userData || !userData.organization.letsLaunchAccess) {
            navigate("/login");
          }
          //todo HERE REVOKE FULL ACCESS IF DUE DATE IS PAST
          setCurrentWorkspace(userData.ownedWorkspaces[0]);
          await fetchProjects(userData.ownedWorkspaces[0]._id);
        } catch (error) {
          console.error("Failed to fetch user:", error);
        }
      } else {
        navigate("/login");
      }
    };

    fetchUser(); // Call the function to fetch user
    //fetchProjects(); // Call the function to fetch projects
  }, []);

  useEffect(() => {
    const path = location.pathname.split("/").filter((x) => x);
    if (projectId && currentProject) {
      path[1] = currentProject.name;
    }
    if (videoPageId && currentProject) {
      const videoPage = currentProject.videoPages?.find(
        (videoPage) => videoPage._id === videoPageId
      );
      path[2] = "video pages";
      path[3] = videoPage?.name || "";
    }
    setPathNames([...path]);
  }, [videoPageId, currentProject, projectId, location]);

  useEffect(() => {
    if (!currentWorkspace) return;

    fetchProjects(currentWorkspace._id);
  }, [user, currentWorkspace]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Upgrade Banner - Full width at the top */}
      {showUpgradeBanner && (
        <Box
          sx={{
            backgroundColor: THEME_COLORS.yellow,
            color: THEME_COLORS.black,
            padding: "8px 16px",
            display: "flex",
            justifyContent: "left",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 1100,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            width: "100%",
          }}
        >
          <Typography variant="body2">
            Unlock the full potential! Upgrade now for premium features and
            unlimited access.
          </Typography>
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/plan-selection")}
              sx={{
                ml: 2,
                backgroundColor: THEME_COLORS.black,
                color: THEME_COLORS.white,
                "&:hover": {
                  backgroundColor: THEME_COLORS.darkGray,
                },
              }}
            >
              Upgrade Plan
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flex: 1 }}>
        <Sidebar />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Main content area with breadcrumbs and outlet */}
          <Box sx={{ p: 3 }}>
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{ color: THEME_COLORS.darkGray, fontSize: "12px", mb: 2 }}
            >
              {pathnames.length === 0 ? (
                <Typography color="textPrimary">
                  {currentWorkspace?.name}
                </Typography>
              ) : (
                pathnames.map((value, index) => (
                  <Typography
                    key={index}
                    style={{
                      color: THEME_COLORS.darkGray2,
                      fontSize: "13px",
                      textTransform: "capitalize",
                    }}
                  >
                    {value}
                  </Typography>
                ))
              )}
            </Breadcrumbs>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
